import React, { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { toast } from './Toast';
import { STAGE_COLORS } from '../lib/constants';

const DEV_ROOM_ID = '00000000-0000-0000-0000-000000000002';

// ── Coordinates ─────────────────────────────────────────────────────────────
// The lot is a FIXED logical canvas (LW × LH logical px) — identical on every
// device, like a real floor plan. To fit any screen without reflowing, the
// canvas is rendered at a pan/zoom transform instead of being stretched:
// desktop and mobile show the exact same layout, just at whatever scale fits
// (or whatever the user has zoomed/panned to). The grid is fixed logical px
// too, so it's always a true square and dragging always snaps evenly.
// LW×LH IS the grid — the one fixed floor-plan rectangle every space and car
// lives inside, on every device. Nothing is draggable/placeable outside it.
const LW = 1200, LH = 760;
const GRID = 10;                 // logical px — square grid cell (visual + snap)
const CAR_W = 170, CAR_H = 110;  // default size of a free-standing car
const MIN_ZOOM = 0.3, MAX_ZOOM = 3;
const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));
const snap = v => Math.round(v / GRID) * GRID;

const ROW_X = [40, 260, 480, 700, 920]; // 5 evenly-spaced stall columns
const DEFAULT_LAYOUT = {
  els: [
    { id: 'b1', type: 'building', gx: 40, gy: 30, gw: 1120, gh: 140, text: 'BODY SHOP' },
    { id: 'l1', type: 'label', gx: 40, gy: 195, gw: 260, gh: 32, text: 'FRONT LOT' },
    ...ROW_X.map((gx, i) => ({ id: 's' + (i + 1), type: 'stall', gx, gy: 230, gw: 180, gh: 200, text: String(i + 1) })),
    { id: 'l2', type: 'label', gx: 40, gy: 450, gw: 340, gh: 32, text: '▸ STREET / OVERFLOW' },
    ...ROW_X.map((gx, i) => ({ id: 's' + (i + 6), type: 'stall', gx, gy: 490, gw: 180, gh: 180, text: String(i + 6) })),
  ],
  cars: [],
};

const LS_KEY = 'shoptrack_dev_parking_v2';

export default function ParkingLot({ room, claims = [], onOpenClaim, desktop, bgImage, editMode, setEditMode }) {
  const isDev = room.id === DEV_ROOM_ID;
  const isAdmin = room.role === 'admin';
  const viewportRef = useRef(null);  // outer clipped area — receives pan/zoom gestures
  const canvasRef = useRef(null);    // inner LW×LH div — gets the pan/zoom CSS transform

  const [layout, setLayout] = useState(DEFAULT_LAYOUT);
  const [loading, setLoading] = useState(true);
  const [popover, setPopover] = useState(null);       // { kind:'car'|'el', id }
  const [hoverStall, setHoverStall] = useState(null);  // stall id highlighted during a drag
  const [ghost, setGhost] = useState(null);           // tray drag: { claimId, x, y }
  const [view, setView] = useState({ zoom: 1, panX: 0, panY: 0 });

  // editMode is a controlled prop now (FloorPage's mobile tab bar has its own Edit Lot
  // button in place of Tools) — whichever side flips it, close any open popover.
  useEffect(() => { setPopover(null); }, [editMode]);

  // The lot belongs to the active room — even in merged-floor mode only park this room's cars.
  const roomClaims = claims.filter(c => !c.room_id || c.room_id === room.id);
  const claimById = id => roomClaims.find(c => c.id === id);
  const placedIds = layout.cars.map(c => c.claim_id);
  const trayCars = roomClaims.filter(c => !placedIds.includes(c.id) && c.status !== 'Closed');

  // ── load + realtime ──
  useEffect(() => {
    if (isDev) {
      try { const s = localStorage.getItem(LS_KEY); setLayout(s ? JSON.parse(s) : DEFAULT_LAYOUT); }
      catch { setLayout(DEFAULT_LAYOUT); }
      setLoading(false);
      return;
    }
    supabase.from('parking_lots').select('layout').eq('room_id', room.id).maybeSingle()
      .then(({ data }) => { setLayout(data?.layout || DEFAULT_LAYOUT); setLoading(false); });
    const ch = supabase.channel(`parking-${room.id}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'parking_lots', filter: `room_id=eq.${room.id}` },
        payload => { if (payload.new?.layout) setLayout(payload.new.layout); })
      .subscribe();
    return () => supabase.removeChannel(ch);
  }, [isDev, room.id]);

  // Save the whole layout (one shared record per room). Called on commit, not on every move.
  // View (pan/zoom) is local display state only — never persisted, never synced.
  const persist = useCallback((next) => {
    setLayout(next);
    if (isDev) { try { localStorage.setItem(LS_KEY, JSON.stringify(next)); } catch {} return; }
    supabase.from('parking_lots')
      .upsert({ room_id: room.id, layout: next, updated_at: new Date().toISOString() })
      .then(({ error }) => { if (error) toast('Could not save the lot — check your connection.', 'error'); });
  }, [isDev, room.id]);

  // ── fit-to-screen (once, on first layout measurement) ──
  // The area every drawn space + parked car actually occupies, padded a bit
  // so nothing sits flush against the screen edge. Falls back to the fit
  // reference box when the lot is empty (nothing to bound around yet).
  // Takes the layout explicitly (not the `layout` state) so this stays
  // correct when called from the drag engine below, which reads layout via
  // a ref to avoid re-attaching its window listeners every frame — see the
  // comment above `layoutRef`.
  function getContentBounds(lay) {
    const pad = 60;
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    lay.els.forEach(el => {
      minX = Math.min(minX, el.gx); minY = Math.min(minY, el.gy);
      maxX = Math.max(maxX, el.gx + el.gw); maxY = Math.max(maxY, el.gy + el.gh);
    });
    lay.cars.forEach(car => {
      const r = carRect(lay, car);
      minX = Math.min(minX, r.gx); minY = Math.min(minY, r.gy);
      maxX = Math.max(maxX, r.gx + r.gw); maxY = Math.max(maxY, r.gy + r.gh);
    });
    if (!isFinite(minX)) return { x: 0, y: 0, w: LW, h: LH };
    return { x: minX - pad, y: minY - pad, w: (maxX - minX) + pad * 2, h: (maxY - minY) + pad * 2 };
  }
  // Mobile's zoom floor: whatever fits ALL drawn content on screen at once.
  // Below that there'd be nothing more to reveal, so pinching/scrolling out
  // further than "everything is already visible" is blocked here.
  function coverZoom(lay) {
    const el = viewportRef.current; if (!el) return MIN_ZOOM;
    const { width, height } = el.getBoundingClientRect();
    if (!width || !height) return MIN_ZOOM;
    const b = getContentBounds(lay);
    return Math.min(width / b.w, height / b.h);
  }
  // Clamps ONLY the pan offset for a fixed zoom — used while dragging, so a
  // plain pan never touches zoom at all. Desktop is bounded against the grid
  // itself (LW×LH): when it already fits inside the screen (the normal
  // desktop case), this locks pan dead center. Mobile is bounded against the
  // same drawn-content box `fitToScreen`/`coverZoom` use — bounding it
  // against LW×LH instead (a fixed box the actual content rarely fills
  // exactly) was recentering on the wrong rectangle and made the view jump
  // the instant you zoomed or panned after the initial auto-fit.
  function clampPan(zoom, panX, panY, lay) {
    const el = viewportRef.current; if (!el) return { zoom, panX, panY };
    const { width, height } = el.getBoundingClientRect();
    const box = desktop ? { x: 0, y: 0, w: LW, h: LH } : getContentBounds(lay);
    const cw = box.w * zoom, ch = box.h * zoom;
    const px = cw <= width ? width / 2 - (box.x + box.w / 2) * zoom : clamp(panX, width - cw - box.x * zoom, -box.x * zoom);
    const py = ch <= height ? height / 2 - (box.y + box.h / 2) * zoom : clamp(panY, height - ch - box.y * zoom, -box.y * zoom);
    return { zoom, panX: px, panY: py };
  }
  // Clamps zoom AND pan together — used only for actual zoom-changing
  // interactions (wheel/pinch/buttons, mobile only), so the canvas can never
  // zoom out past "everything is visible". Never used for a plain pan.
  function clampView(zoom, panX, panY, lay) {
    const z = clamp(zoom, coverZoom(lay), MAX_ZOOM);
    return clampPan(z, panX, panY, lay);
  }
  const fitToScreen = useCallback(() => {
    const el = viewportRef.current; if (!el) return;
    const { width, height } = el.getBoundingClientRect();
    if (!width || !height) return;
    if (desktop) {
      // Fixed real 1:1 size, always — no stretching, no shrinking. The grid
      // background fills the whole screen on its own (see below), so there's
      // no margin to eliminate; drag reveals the rest if the window is
      // smaller than the lot.
      setView({ zoom: 1, panX: (width - LW) / 2, panY: (height - LH) / 2 });
      return;
    }
    // Mobile: fit to the ACTUAL drawn content, not the fixed reference box —
    // so the whole lot is visible at once and it never starts zoomed out
    // further than that.
    const b = getContentBounds(layout);
    const z = clamp(Math.min(width / b.w, height / b.h), MIN_ZOOM, MAX_ZOOM);
    setView({ zoom: z, panX: width / 2 - (b.x + b.w / 2) * z, panY: height / 2 - (b.y + b.h / 2) * z });
  }, [desktop, layout]);
  // Keeps re-fitting whenever the screen actually changes size — window
  // resize, phone rotation, the mobile browser's address bar hiding/showing —
  // not just once at mount. On mobile this stops once the user manually
  // pans/zooms (so we don't rip their view away mid-interaction) — the Fit
  // button re-enables it. Desktop has no zoom and no Fit button, so a manual
  // pan there is only ever a temporary look-around: an actual window resize
  // always re-centers and re-covers, guaranteeing no edge ever stays hidden.
  const userAdjusted = useRef(false);
  useEffect(() => {
    const el = viewportRef.current; if (!el) return;
    const ro = new ResizeObserver(entries => {
      const { width, height } = entries[0].contentRect;
      if (width > 0 && height > 0 && (desktop || !userAdjusted.current)) fitToScreen();
    });
    ro.observe(el);
    // Safety nets for the very first fit: the ResizeObserver's initial callback
    // can fire during an early layout pass before the page has fully settled —
    // e.g. before the Barlow Condensed header font swaps in and reflows the
    // toolbar above, changing how much room is actually left here. Without
    // these, a too-small first fit sticks until some unrelated click happens
    // to trigger a real resize event.
    let raf2;
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => { if (desktop || !userAdjusted.current) fitToScreen(); });
    });
    if (document.fonts?.ready) {
      document.fonts.ready.then(() => { if (desktop || !userAdjusted.current) fitToScreen(); });
    }
    return () => { ro.disconnect(); cancelAnimationFrame(raf1); cancelAnimationFrame(raf2); };
  }, [fitToScreen, desktop]);

  function zoomBy(factor) {
    userAdjusted.current = true;
    const el = viewportRef.current; if (!el) return;
    const { width, height } = el.getBoundingClientRect();
    setView(v => {
      const target = v.zoom * factor;
      const px = width / 2, py = height / 2;
      const lx = (px - v.panX) / v.zoom, ly = (py - v.panY) / v.zoom;
      return clampView(target, px - lx * target, py - ly * target, layout);
    });
  }
  function onWheel(e) {
    userAdjusted.current = true;
    e.preventDefault();
    const rect = viewportRef.current.getBoundingClientRect();
    const px = e.clientX - rect.left, py = e.clientY - rect.top;
    const factor = Math.exp(-e.deltaY * 0.0015);
    setView(v => {
      const target = v.zoom * factor;
      const lx = (px - v.panX) / v.zoom, ly = (py - v.panY) / v.zoom;
      return clampView(target, px - lx * target, py - ly * target, layout);
    });
  }
  function viewCenterLogical() {
    const el = viewportRef.current; if (!el) return { x: LW / 2, y: LH / 2 };
    const { width, height } = el.getBoundingClientRect();
    return { x: (width / 2 - view.panX) / view.zoom, y: (height / 2 - view.panY) / view.zoom };
  }

  // ── one/two-finger gestures on empty canvas: pan + pinch-to-zoom ──
  const pointers = useRef(new Map());
  const pinch = useRef(null);
  function bgDown(e) {
    userAdjusted.current = true;
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pointers.current.size === 2) {
      drag.current = null; // a pan in progress gives way to a pinch
      const pts = [...pointers.current.values()];
      const dist = Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y) || 1;
      const mid = { x: (pts[0].x + pts[1].x) / 2, y: (pts[0].y + pts[1].y) / 2 };
      pinch.current = { startDist: dist, startZoom: view.zoom, startPanX: view.panX, startPanY: view.panY, startMid: mid };
    } else if (pointers.current.size === 1 && !drag.current) {
      drag.current = { kind: 'pan', px: e.clientX, py: e.clientY, opanX: view.panX, opanY: view.panY, moved: false };
    }
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch {}
  }
  function bgMove(e) {
    if (!pointers.current.has(e.pointerId)) return;
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pointers.current.size === 2 && pinch.current) {
      const pts = [...pointers.current.values()];
      const dist = Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y) || 1;
      const mid = { x: (pts[0].x + pts[1].x) / 2, y: (pts[0].y + pts[1].y) / 2 };
      const rect = viewportRef.current.getBoundingClientRect();
      const pr = pinch.current;
      const target = pr.startZoom * (dist / pr.startDist);
      const lx = (pr.startMid.x - rect.left - pr.startPanX) / pr.startZoom;
      const ly = (pr.startMid.y - rect.top - pr.startPanY) / pr.startZoom;
      setView(clampView(target, (mid.x - rect.left) - lx * target, (mid.y - rect.top) - ly * target, layout));
    }
  }
  function bgUp(e) {
    pointers.current.delete(e.pointerId);
    if (pointers.current.size < 2) pinch.current = null;
  }

  // ── geometry helpers (logical px, 0–LW / 0–LH) ──
  const toLogical = (clientX, clientY) => {
    const rect = canvasRef.current.getBoundingClientRect();
    return { x: (clientX - rect.left) / rect.width * LW, y: (clientY - rect.top) / rect.height * LH };
  };
  // Take the layout explicitly (rather than closing over the `layout` state)
  // so these can be called from inside the drag engine below using a ref's
  // always-current value, without the drag engine needing `layout` in its
  // effect dependencies.
  const emptyStallAt = (lay, x, y, exceptCarId) => lay.els.find(el =>
    el.type === 'stall' && x >= el.gx && x <= el.gx + el.gw && y >= el.gy && y <= el.gy + el.gh &&
    !lay.cars.some(c => c.spot_id === el.id && c.claim_id !== exceptCarId));

  // Where a car actually renders: filling a stall, or free-standing at its own gx/gy.
  const carRect = (lay, car) => {
    if (car.spot_id) {
      const st = lay.els.find(e => e.id === car.spot_id);
      if (st) return { gx: st.gx, gy: st.gy, gw: st.gw, gh: st.gh }; // fully fill the space
    }
    return { gx: car.gx, gy: car.gy, gw: CAR_W, gh: CAR_H };
  };
  // The logical rectangle currently ON SCREEN for a given view (pan/zoom) —
  // spaces and cars are draggable/placeable exactly within this, matching
  // whatever grid is actually visible on THIS device right now (confirmed:
  // a coworker on a smaller screen gets less room than an ultrawide would).
  function boundsFor(v) {
    const el = viewportRef.current;
    if (!el) return { x: 0, y: 0, w: LW, h: LH };
    const { width, height } = el.getBoundingClientRect();
    return { x: -v.panX / v.zoom, y: -v.panY / v.zoom, w: width / v.zoom, h: height / v.zoom };
  }

  // ── drag engine (elements, resize, placed cars, tray→canvas, background pan) ──
  // The window-level pointermove/pointerup listeners below are attached ONCE,
  // not re-attached on every drag step — they read the latest layout/view via
  // these refs instead of closing over the `layout`/`view` state directly, so
  // the effect doesn't need either in its dependency array. Tearing down and
  // rebuilding native window listeners on every single frame of an active
  // drag (which is what depending on `layout` caused, since every drag step
  // calls setLayout) was wasteful churn, not the intended design.
  const layoutRef = useRef(layout);
  useEffect(() => { layoutRef.current = layout; }, [layout]);
  const viewRef = useRef(view);
  useEffect(() => { viewRef.current = view; }, [view]);
  const drag = useRef(null);
  useEffect(() => {
    function move(e) {
      const d = drag.current; if (!d) return;

      if (d.kind === 'pan') {
        // Same jitter gate as el/car drags below — a mere touch reports a
        // few px of movement even when the finger never meant to drag, and
        // without this the view nudged on every single tap.
        const slop = desktop ? 4 : 10;
        const wasMoved = d.moved;
        d.moved = d.moved || Math.abs(e.clientX - d.px) > slop || Math.abs(e.clientY - d.py) > slop;
        if (!d.moved) return;
        if (!wasMoved) { d.px = e.clientX; d.py = e.clientY; } // re-anchor to here so crossing the threshold doesn't jump
        // Desktop only ever needs to reach left/right (a wide screen, not a
        // short one) — lock vertical dragging there so it can't drift off-axis.
        setView(v => clampPan(v.zoom, d.opanX + (e.clientX - d.px), desktop ? d.opanY : d.opanY + (e.clientY - d.py), layoutRef.current));
        return;
      }
      const p = toLogical(e.clientX, e.clientY);
      const lay = layoutRef.current;

      if (d.kind === 'tray') {
        setGhost({ claimId: d.claimId, x: e.clientX, y: e.clientY });
        setHoverStall((emptyStallAt(lay, p.x, p.y) || {}).id || null);
        return;
      }
      // A mere touch/click reports a few px of jitter on essentially every
      // device — without this gate, that jitter alone was enough to snap the
      // piece's position the instant you touched it, before any real drag.
      // Nothing moves until the pointer has genuinely crossed the threshold.
      // Touch is noisier than a mouse — real fingers report more jitter on an
      // otherwise-stationary press — so mobile gets a more forgiving slop.
      const slop = desktop ? 4 : 10;
      const wasMoved = d.moved;
      d.moved = d.moved || Math.abs(e.clientX - d.px) > slop || Math.abs(e.clientY - d.py) > slop;
      if (!d.moved) return;
      if (!wasMoved) { d.lx = p.x; d.ly = p.y; } // re-anchor to here so it starts from the current spot, no jump
      const vb = boundsFor(viewRef.current);

      setLayout(prev => {
        if (d.kind === 'el') {
          return { ...prev, els: prev.els.map(el => el.id !== d.id ? el : {
            ...el,
            gx: clamp(snap(d.ogx + (p.x - d.lx)), vb.x, vb.x + vb.w - el.gw),
            gy: clamp(snap(d.ogy + (p.y - d.ly)), vb.y, vb.y + vb.h - el.gh),
          }) };
        }
        if (d.kind === 'resize') {
          return { ...prev, els: prev.els.map(el => el.id !== d.id ? el : {
            ...el,
            gw: clamp(snap(d.ogw + (p.x - d.lx)), GRID * 3, vb.x + vb.w - el.gx),
            gh: clamp(snap(d.ogh + (p.y - d.ly)), GRID * 3, vb.y + vb.h - el.gy),
          }) };
        }
        if (d.kind === 'car') {
          setHoverStall((emptyStallAt(prev, p.x, p.y, d.id) || {}).id || null);
          return { ...prev, cars: prev.cars.map(c => c.id !== d.id ? c : {
            ...c, spot_id: null,
            gx: clamp(snap(d.ogx + (p.x - d.lx)), vb.x, vb.x + vb.w - CAR_W),
            gy: clamp(snap(d.ogy + (p.y - d.ly)), vb.y, vb.y + vb.h - CAR_H),
          }) };
        }
        return prev;
      });
    }
    function up(e) {
      const d = drag.current; if (!d) return;
      drag.current = null;
      if (d.kind === 'pan') return; // view state already committed live, nothing to persist

      const p = toLogical(e.clientX, e.clientY);
      const lay = layoutRef.current;

      if (d.kind === 'tray') {
        setGhost(null); setHoverStall(null);
        const vRect = viewportRef.current.getBoundingClientRect();
        const inside = e.clientX >= vRect.left && e.clientX <= vRect.right && e.clientY >= vRect.top && e.clientY <= vRect.bottom;
        if (!inside) return;
        const st = emptyStallAt(lay, p.x, p.y);
        const vb = boundsFor(viewRef.current);
        const car = st
          ? { id: 'car-' + Date.now(), claim_id: d.claimId, spot_id: st.id, gx: 0, gy: 0 }
          : { id: 'car-' + Date.now(), claim_id: d.claimId, spot_id: null,
              gx: clamp(snap(p.x - CAR_W / 2), vb.x, vb.x + vb.w - CAR_W), gy: clamp(snap(p.y - CAR_H / 2), vb.y, vb.y + vb.h - CAR_H) };
        persist({ ...lay, cars: [...lay.cars, car] });
        return;
      }
      setHoverStall(null);
      if (!d.moved) { setPopover({ kind: d.kind === 'car' ? 'car' : 'el', id: d.id }); return; } // a tap
      setLayout(prev => {
        if (d.kind === 'car') {
          const st = emptyStallAt(prev, p.x, p.y, d.id);
          const next = { ...prev, cars: prev.cars.map(c => c.id !== d.id ? c : (st ? { ...c, spot_id: st.id } : c)) };
          persist(next);
          return next;
        }
        persist(prev);
        return prev;
      });
    }
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    return () => { window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', up); };
  }, [desktop, persist]);

  function startEl(e, el) {
    if (!editMode) return;
    e.stopPropagation();
    userAdjusted.current = true; // touching a piece counts as taking control of the view, same as touching background
    const p = toLogical(e.clientX, e.clientY);
    drag.current = { kind: 'el', id: el.id, px: e.clientX, py: e.clientY, lx: p.x, ly: p.y, ogx: el.gx, ogy: el.gy, moved: false };
  }
  function startResize(e, el) {
    e.stopPropagation();
    userAdjusted.current = true;
    const p = toLogical(e.clientX, e.clientY);
    drag.current = { kind: 'resize', id: el.id, px: e.clientX, py: e.clientY, lx: p.x, ly: p.y, ogw: el.gw, ogh: el.gh, moved: true };
  }
  function startCar(e, car) {
    e.stopPropagation();
    userAdjusted.current = true;
    const r = carRect(layout, car);
    const p = toLogical(e.clientX, e.clientY);
    drag.current = { kind: 'car', id: car.id, px: e.clientX, py: e.clientY, lx: p.x, ly: p.y, ogx: r.gx, ogy: r.gy, moved: false };
  }
  function startTray(e, claimId) {
    e.preventDefault();
    drag.current = { kind: 'tray', claimId };
    setGhost({ claimId, x: e.clientX, y: e.clientY });
  }

  // ── element ops ──
  function addEl(type) {
    const base = {
      stall: { gw: 180, gh: 200, text: String(layout.els.filter(e => e.type === 'stall').length + 1) },
      building: { gw: 400, gh: 160, text: 'BUILDING' },
      zone: { gw: 420, gh: 260, text: 'ZONE' },
      label: { gw: 300, gh: 44, text: 'LABEL' },
    }[type];
    const vc = viewCenterLogical();
    const vb = boundsFor(view);
    const el = {
      id: 'e' + Date.now(), type, ...base,
      gx: clamp(snap(vc.x - base.gw / 2), vb.x, vb.x + vb.w - base.gw),
      gy: clamp(snap(vc.y - base.gh / 2), vb.y, vb.y + vb.h - base.gh),
    };
    persist({ ...layout, els: [...layout.els, el] });
  }
  function renameEl(id, text) { persist({ ...layout, els: layout.els.map(e => e.id === id ? { ...e, text } : e) }); }
  function deleteEl(id) {
    const st = layout.els.find(e => e.id === id);
    const cars = layout.cars.map(c => c.spot_id === id ? { ...c, spot_id: null, gx: st ? st.gx : 40, gy: st ? st.gy : 40 } : c);
    persist({ els: layout.els.filter(e => e.id !== id), cars });
    setPopover(null);
  }
  function removeCar(id) { persist({ ...layout, cars: layout.cars.filter(c => c.id !== id) }); setPopover(null); }
  function resetLayout() {
    if (window.confirm('Reset the lot to the default layout? This clears your spaces and takes every car off the lot.')) persist(DEFAULT_LAYOUT);
  }

  const popEl = popover?.kind === 'el' ? layout.els.find(e => e.id === popover.id) : null;
  const popCar = popover?.kind === 'car' ? layout.cars.find(c => c.id === popover.id) : null;
  const zoomBtn = { width: 30, height: 30, borderRadius: 8, background: 'transparent', border: 'none', color: 'var(--text2)', fontSize: 17, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 };

  // Shared between the desktop (floating-panel) and mobile (stacked-row) layouts below,
  // so the two arrangements can't drift apart on what they actually render.
  const editPieceButtons = (
    <>
      {[['stall', 'Add Space', <><rect x="4" y="4" width="16" height="16" rx="1"/></>],
        ['building', 'Add Building', <><path d="M3 21h18"/><path d="M5 21V7l8-4v18"/><path d="M19 21V11l-6-4"/></>],
        ['zone', 'Add Zone', <><rect x="4" y="4" width="16" height="16" rx="2" strokeDasharray="3 2"/></>],
        ['label', 'Add Label', <><path d="M4 7h16M4 12h10M4 17h13"/></>]].map(([type, label, ico]) => (
        <button key={type} onClick={() => addEl(type)} className="raise-3d" style={{ display: 'flex', alignItems: 'center', gap: 7, background: 'var(--bg3)', border: '1px solid var(--border)', color: 'var(--text2)', borderRadius: 9, padding: '7px 12px', fontSize: 12.5, fontWeight: 700, cursor: 'pointer' }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--orange)" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">{ico}</svg>{label}
        </button>
      ))}
      <button onClick={resetLayout} className="raise-3d" style={{ display: 'flex', alignItems: 'center', gap: 7, marginLeft: 'auto', background: 'var(--bg3)', border: '1px solid var(--border)', color: 'var(--text3)', borderRadius: 9, padding: '7px 12px', fontSize: 12.5, fontWeight: 700, cursor: 'pointer' }}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 2v6h6"/><path d="M3.51 15a9 9 0 1 0 2.13-9.36L3 8"/></svg>Reset
      </button>
    </>
  );

  const lotPieces = (
    <>
      {layout.els.map(el => {
        const isStall = el.type === 'stall', isBuilding = el.type === 'building', isZone = el.type === 'zone';
        const highlight = hoverStall === el.id;
        return (
          <div key={el.id} className="pk-el" onPointerDown={e => startEl(e, el)} style={{
            left: el.gx + 'px', top: el.gy + 'px', width: el.gw + 'px', height: el.gh + 'px',
            background: isBuilding ? 'repeating-linear-gradient(45deg, var(--bg4) 0 9px, var(--bg3) 9px 18px)'
              : isStall ? (highlight ? 'rgba(249,115,22,0.14)' : 'var(--bg3)')
              : isZone ? 'var(--bg3)' : 'transparent',
            border: isBuilding ? '1px solid var(--border)'
              : isStall ? `1.5px solid ${highlight ? 'var(--orange)' : 'var(--border)'}`
              : isZone ? '1px dashed var(--border)' : 'none',
            borderRadius: isBuilding ? 6 : isZone ? 8 : 4,
            display: 'flex', alignItems: isZone ? 'flex-start' : 'center', justifyContent: 'center',
            padding: isZone ? '6px 8px' : 0,
          }}>
            <span style={{
              fontFamily: isStall ? 'var(--font-mono)' : 'var(--font-display)', fontWeight: 700, color: 'var(--text3)',
              fontSize: isBuilding ? 15 : isZone ? 12 : el.type === 'label' ? 13 : 10,
              letterSpacing: isStall ? '0.05em' : '1.5px', whiteSpace: 'nowrap',
              alignSelf: isStall ? 'flex-start' : undefined, marginTop: isStall ? 4 : 0,
            }}>{el.text}</span>
            {editMode && <>
              <div className="pk-del" onPointerDown={e => e.stopPropagation()} onClick={e => { e.stopPropagation(); deleteEl(el.id); }}>×</div>
              <div className="pk-size" onPointerDown={e => startResize(e, el)} />
            </>}
          </div>
        );
      })}

      {layout.cars.map(car => {
        const c = claimById(car.claim_id); if (!c) return null;
        const r = carRect(layout, car); const col = STAGE_COLORS[c.status] || '#f97316';
        return (
          <div key={car.id} className="pk-chip" onPointerDown={e => startCar(e, car)} style={{
            position: 'absolute', left: r.gx + 'px', top: r.gy + 'px', width: r.gw + 'px', height: r.gh + 'px',
            background: 'var(--bg3)', border: `1px solid ${col}66`, borderLeft: `3px solid ${col}`, borderRadius: 8,
            boxShadow: '0 3px 0 rgba(16,16,20,0.8), 0 6px 12px rgba(0,0,0,0.4)', cursor: editMode ? 'grab' : 'pointer',
            padding: 6, overflow: 'hidden', display: 'flex', flexDirection: 'column', justifyContent: 'center',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <span style={{ width: 7, height: 7, borderRadius: '50%', background: col, flexShrink: 0 }} />
              <span style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 13, lineHeight: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{(c.car || 'Car').replace(/^\d+\s/, '')}</span>
            </div>
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 9, fontWeight: 700, letterSpacing: '0.8px', color: 'var(--text2)', marginTop: 4, whiteSpace: 'nowrap' }}>{c.plate || '—'}</span>
            {c.insurer && <span style={{ fontSize: 9, color: 'var(--text3)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.insurer}</span>}
          </div>
        );
      })}
    </>
  );

  const trayList = (
    <>
      {trayCars.length === 0 && (
        <div style={{ fontSize: 12.5, color: 'var(--text3)', padding: desktop ? '8px 2px' : '0 4px', alignSelf: 'center' }}>Every car is on the lot.</div>
      )}
      {trayCars.map(c => {
        const col = STAGE_COLORS[c.status] || '#f97316';
        return (
          <div key={c.id} className="pk-chip" onPointerDown={e => startTray(e, c.id)} style={{
            flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 4, cursor: 'grab', userSelect: 'none',
            background: 'var(--bg3)', border: `1px solid var(--border)`, borderLeft: `3px solid ${col}`, borderRadius: 10,
            padding: '8px 11px', minWidth: desktop ? 'auto' : 120,
            boxShadow: '0 3px 0 rgba(16,16,20,0.8), 0 6px 12px rgba(0,0,0,0.38)', touchAction: 'none',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ width: 7, height: 7, borderRadius: '50%', background: col, flexShrink: 0 }} />
              <span style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 14, lineHeight: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{(c.car || 'Car').replace(/^\d+\s/, '')}</span>
            </div>
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 9.5, fontWeight: 700, letterSpacing: '0.8px', color: 'var(--text2)' }}>{c.plate || '—'}</span>
            {c.insurer && <span style={{ fontSize: 9.5, color: 'var(--text3)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.insurer}</span>}
          </div>
        );
      })}
    </>
  );

  // ── render ──
  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%', minWidth: 0, minHeight: 0, overflow: 'hidden', background: bgImage ? 'transparent' : 'var(--bg)' }}>
      <style>{`
        .pk-el{position:absolute}
        .pk-el .pk-del{position:absolute;top:-9px;right:-9px;width:20px;height:20px;border-radius:50%;background:var(--red);color:#fff;border:2px solid var(--bg2);display:flex;align-items:center;justify-content:center;font-size:12px;font-weight:800;cursor:pointer;z-index:9}
        .pk-el .pk-size{position:absolute;right:-7px;bottom:-7px;width:15px;height:15px;border-radius:4px;background:var(--orange);border:2px solid var(--bg2);cursor:nwse-resize;z-index:9}
        .pk-canvas.editing .pk-el{outline:1.4px solid rgba(249,115,22,0.4);outline-offset:1px;cursor:grab}
        .pk-chip{transition:transform .1s, box-shadow .1s}
        .pk-chip:active{transform:translateY(2px)}
        .pk-tray::-webkit-scrollbar{width:8px;height:8px}
        .pk-tray::-webkit-scrollbar-thumb{background:var(--bg4);border-radius:4px}
        .pk-zoomctl button:active{background:rgba(255,255,255,0.14)}
      `}</style>

      {desktop ? (
        /* Desktop: the canvas fills this whole area edge-to-edge, all the way under the
           tray on the right — the toolbar/hint controls and the tray float on top of it
           as translucent panels (same idea as the mobile zoom controls) instead of being
           separate rows/columns that squeeze the canvas down to a smaller box. */
        <div style={{ flex: 1, minWidth: 0, minHeight: 0, position: 'relative', overflow: 'hidden' }}>
          <div
            ref={viewportRef}
            onPointerDown={bgDown} onPointerMove={bgMove} onPointerUp={bgUp} onPointerCancel={bgUp}
            style={{
              position: 'absolute', inset: 0, overflow: 'hidden',
              borderRadius: 12, border: '1px solid var(--border)',
              touchAction: 'none', cursor: !editMode ? 'grab' : 'default',
              userSelect: 'none', WebkitUserSelect: 'none',
              // Matches whatever floor background color/image is set, same as every other
              // translucent panel on this page — it used to always be solid var(--bg2)
              // regardless, which is why it looked like a disconnected surface.
              background: bgImage ? 'rgba(20,20,22,0.5)' : 'var(--bg2)',
              backgroundImage: `linear-gradient(${editMode ? 'rgba(249,115,22,0.10)' : 'rgba(255,255,255,0.035)'} 1px, transparent 1px), linear-gradient(90deg, ${editMode ? 'rgba(249,115,22,0.10)' : 'rgba(255,255,255,0.035)'} 1px, transparent 1px)`,
              backgroundSize: `${GRID}px ${GRID}px`,
            }}
          >
            {loading && <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 6 }}><div className="spinner" /></div>}
            <div ref={canvasRef} className={`pk-canvas${editMode ? ' editing' : ''}`} style={{
              position: 'absolute', left: 0, top: 0, width: LW, height: LH,
              transform: `translate(${view.panX}px, ${view.panY}px) scale(${view.zoom})`, transformOrigin: '0 0',
              background: 'transparent',
            }}>
              {lotPieces}
            </div>
          </div>

          {/* controls — plain floating text/buttons, top-left, no card behind them */}
          <div style={{ position: 'absolute', top: 20, left: 32, zIndex: 20, maxWidth: 420, display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ fontSize: 12.5, color: 'var(--text2)', textShadow: '0 1px 3px rgba(0,0,0,0.9), 0 0 10px rgba(0,0,0,0.6)' }}>
                <b style={{ color: 'var(--text)', fontWeight: 700 }}>{layout.cars.length}</b> parked · <b style={{ color: 'var(--text)', fontWeight: 700 }}>{trayCars.length}</b> to place
              </div>
              {isAdmin && (
                <button onClick={() => setEditMode(v => !v)} className={editMode ? 'raise-3d-orange' : 'raise-3d'} style={{ display: 'flex', alignItems: 'center', gap: 7, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 700, padding: '8px 13px', borderRadius: 9, background: editMode ? 'var(--orange)' : 'var(--bg3)', color: editMode ? '#16100b' : 'var(--text2)' }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z"/></svg>
                  {editMode ? 'Done' : 'Edit Lot'}
                </button>
              )}
            </div>
            {editMode && (
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                {editPieceButtons}
              </div>
            )}
          </div>

          {/* cars tray — floating panel, right edge, full height */}
          <div className="pk-tray" style={{
            position: 'absolute', top: 0, right: 0, bottom: 0, width: 230, zIndex: 15,
            display: 'flex', flexDirection: 'column', gap: 8,
            padding: '20px 20px 20px 24px', overflowX: 'hidden', overflowY: 'auto',
            background: 'linear-gradient(to left, rgba(12,12,15,0.78), rgba(12,12,15,0.5) 70%, transparent)',
          }}>
            <div style={{ fontSize: 10, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.12em', fontWeight: 700, flexShrink: 0, marginBottom: 2 }}>Cars to place</div>
            {trayList}
          </div>
        </div>
      ) : (
        /* Mobile: same idea as desktop above — the canvas fills the whole area, and the
           toolbar/hint controls and the cars tray float on top of it as translucent
           panels instead of being separate rows that push the canvas into a smaller box. */
        <div style={{ flex: 1, minWidth: 0, minHeight: 0, position: 'relative', overflow: 'hidden' }}>
          <div
            ref={viewportRef}
            onPointerDown={bgDown} onPointerMove={bgMove} onPointerUp={bgUp} onPointerCancel={bgUp}
            onWheel={onWheel}
            style={{
              position: 'absolute', inset: 0, overflow: 'hidden',
              borderRadius: 12, border: '1px solid var(--border)',
              touchAction: 'none', cursor: !editMode ? 'grab' : 'default',
              userSelect: 'none', WebkitUserSelect: 'none',
              // The grid dots live here now, not on the canvas below — scaled and offset
              // to track the exact same pan/zoom, so the pattern reads as one continuous
              // full-screen surface instead of a bounded box with a visible edge. Same
              // trick desktop uses (viewport paints the grid, canvas stays transparent);
              // mobile just has to keep it in sync with pan/zoom since mobile actually zooms.
              background: bgImage ? 'rgba(20,20,22,0.5)' : 'var(--bg2)',
              backgroundImage: `linear-gradient(${editMode ? 'rgba(249,115,22,0.10)' : 'rgba(255,255,255,0.035)'} 1px, transparent 1px), linear-gradient(90deg, ${editMode ? 'rgba(249,115,22,0.10)' : 'rgba(255,255,255,0.035)'} 1px, transparent 1px)`,
              backgroundSize: `${GRID * view.zoom}px ${GRID * view.zoom}px`,
              backgroundPosition: `${view.panX}px ${view.panY}px`,
            }}
          >
            {loading && <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 6 }}><div className="spinner" /></div>}

            <div ref={canvasRef} className={`pk-canvas${editMode ? ' editing' : ''}`} style={{
              position: 'absolute', left: 0, top: 0, width: LW, height: LH,
              transform: `translate(${view.panX}px, ${view.panY}px) scale(${view.zoom})`, transformOrigin: '0 0',
              background: 'transparent',
            }}>
              {lotPieces}
            </div>
          </div>

          {/* controls — plain floating text/buttons, top edge, no card behind them */}
          <div style={{ position: 'absolute', top: 12, left: 12, right: 12, zIndex: 20, display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={{ fontSize: 12.5, color: 'var(--text2)', textShadow: '0 1px 3px rgba(0,0,0,0.9), 0 0 10px rgba(0,0,0,0.6)' }}>
              <b style={{ color: 'var(--text)', fontWeight: 700 }}>{layout.cars.length}</b> parked · <b style={{ color: 'var(--text)', fontWeight: 700 }}>{trayCars.length}</b> to place
            </div>
            {editMode && (
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                {editPieceButtons}
              </div>
            )}
          </div>

          {/* cars tray — floating panel, bottom edge */}
          <div className="pk-tray" style={{
            position: 'absolute', left: 12, right: 12, bottom: 12, zIndex: 15,
            display: 'flex', flexDirection: 'row', gap: 8,
            padding: '10px 12px', overflowX: 'auto', overflowY: 'hidden',
            background: 'rgba(20,20,22,0.72)', backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)',
            border: '1px solid rgba(255,255,255,0.08)', borderRadius: 12,
            boxShadow: '0 4px 14px rgba(0,0,0,0.4)',
          }}>
            {trayList}
          </div>

          {/* zoom controls — sits above the tray so the two floating panels don't overlap */}
          <div className="pk-zoomctl" style={{
            position: 'absolute', right: 12, bottom: 92, zIndex: 20,
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1,
            background: 'rgba(20,20,22,0.72)', backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)',
            border: '1px solid rgba(255,255,255,0.08)', borderRadius: 14, padding: '5px 4px',
            boxShadow: '0 4px 14px rgba(0,0,0,0.4)',
          }}>
            <button onClick={() => zoomBy(1.25)} style={zoomBtn}>+</button>
            <button onClick={() => zoomBy(0.8)} style={zoomBtn}>−</button>
            <div style={{ width: 20, height: 1, background: 'rgba(255,255,255,0.14)', margin: '2px 0' }} />
            <button onClick={() => { userAdjusted.current = false; fitToScreen(); }} title="Fit to screen" style={{ ...zoomBtn, fontSize: 13 }}>⤢</button>
          </div>
        </div>
      )}

      {/* tray drag ghost */}
      {ghost && (() => {
        const c = claimById(ghost.claimId); if (!c) return null;
        const col = STAGE_COLORS[c.status] || '#f97316';
        return (
          <div style={{ position: 'fixed', left: ghost.x, top: ghost.y, transform: 'translate(-50%,-50%)', zIndex: 400, pointerEvents: 'none',
            background: 'var(--bg3)', border: `1px solid ${col}`, borderLeft: `3px solid ${col}`, borderRadius: 9, padding: '7px 11px', opacity: 0.92, boxShadow: '0 10px 24px rgba(0,0,0,0.5)' }}>
            <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 13, whiteSpace: 'nowrap' }}>{(c.car || 'Car').replace(/^\d+\s/, '')}</div>
            <div style={{ fontFamily: 'var(--font-mono)', fontSize: 9, fontWeight: 700, color: 'var(--text2)', marginTop: 2 }}>{c.plate || '—'}</div>
          </div>
        );
      })()}

      {/* popover — car actions / rename element */}
      {popover && (
        <div style={{ position: 'absolute', inset: 0, zIndex: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => setPopover(null)}>
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(4,4,6,0.5)' }} />
          <div onClick={e => e.stopPropagation()} style={{ position: 'relative', width: 'min(340px, 88vw)', background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 14, padding: 18, boxShadow: '0 20px 50px rgba(0,0,0,0.5)' }}>
            {popCar && (() => {
              const c = claimById(popCar.claim_id); const col = c ? STAGE_COLORS[c.status] : '#f97316';
              return <>
                <div style={{ fontFamily: 'var(--font-display)', fontSize: 20, fontWeight: 700 }}>{c?.car || 'Car'}</div>
                <div style={{ fontSize: 12.5, color: 'var(--text3)', marginTop: 4, marginBottom: 14 }}><span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: col, marginRight: 6 }} />{c?.status}{popCar.spot_id ? ` · ${(layout.els.find(e => e.id === popCar.spot_id) || {}).text || ''}` : ''}</div>
                <button onClick={() => { onOpenClaim && onOpenClaim(popCar.claim_id); }} className="raise-3d" style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: 12, background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 11, cursor: 'pointer', color: 'var(--text)', fontSize: 14, fontWeight: 700, marginBottom: 8 }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="16" rx="2"/><path d="M3 10h18"/></svg>Open claim
                </button>
                <button onClick={() => removeCar(popCar.id)} className="raise-3d" style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: 12, background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.25)', borderRadius: 11, cursor: 'pointer', color: 'var(--red)', fontSize: 14, fontWeight: 700 }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18M6 6l12 12"/></svg>Take off the lot
                </button>
              </>;
            })()}
            {popEl && <>
              <div style={{ fontFamily: 'var(--font-display)', fontSize: 20, fontWeight: 700, textTransform: 'capitalize' }}>Edit {popEl.type}</div>
              <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 4, marginBottom: 14 }}>Rename or remove this piece of your lot.</div>
              <input defaultValue={popEl.text} maxLength={22} autoFocus onChange={e => renameEl(popEl.id, e.target.value)} style={{ width: '100%', padding: '11px 13px', fontSize: 15, background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 9, color: 'var(--text)', outline: 'none' }} />
              <button onClick={() => deleteEl(popEl.id)} className="raise-3d" style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: 12, background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.25)', borderRadius: 11, cursor: 'pointer', color: 'var(--red)', fontSize: 14, fontWeight: 700, marginTop: 12 }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6"/></svg>Delete
              </button>
              <button onClick={() => setPopover(null)} className="btn btn-primary" style={{ width: '100%', marginTop: 10 }}>Done</button>
            </>}
          </div>
        </div>
      )}
    </div>
  );
}
